import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { FormGroup, Validators } from '@angular/forms';
import {
    LogicStepParameter,
    LogicStepParameterProperty,
} from '@wdx/shared/utils';

export interface ActiveFieldState {
    [code: string]:
        | ((LogicStepParameter | LogicStepParameterProperty) & {
              onChange?: (formGroup: FormGroup, val?: any) => void;
              getValue?: (formGroup: FormGroup) => any;
              destroy$?: Subject<void>;
          })
        | null;
}

export abstract class BaseSegmentedControlStrategy {
    prefixParam$ = new BehaviorSubject<LogicStepParameterProperty | null>(null);
    activeParams$ = new BehaviorSubject<ActiveFieldState>({});

    private lastActive: ActiveFieldState = {};

    protected abstract resolveKind(kind: string | null): ActiveFieldState;

    init(parameter: LogicStepParameter, formGroup: FormGroup): void {
        const kindParam = parameter.properties?.find((p) => p.code === 'Kind');
        if (!kindParam) {
            throw new Error(
                `${this.constructor.name} requires a Kind property`,
            );
        }

        this.prefixParam$.next(kindParam);

        this.lastActive = {};
        this.applyKindChange(formGroup.get(kindParam.code)?.value, formGroup);

        formGroup.get(kindParam.code!)?.valueChanges.subscribe((kind) => {
            this.applyKindChange(kind ?? null, formGroup);
        });
    }

    private applyKindChange(kind: string | null, formGroup: FormGroup): void {
        const nextActive = this.resolveKind(kind);

        Object.entries(this.lastActive).forEach(([code, param]) => {
            const control = formGroup.get(code);
            if (!control) {
                return;
            }

            control.setValidators(null);
            control.reset(null, { emitEvent: true });
            if (param?.destroy$) {
                param.destroy$.next();
                param.destroy$.complete();
            }
        });

        Object.entries(nextActive).forEach(([code, param]) => {
            if (!param) {
                return;
            }
            const control = formGroup.get(code);
            if (!control) {
                return;
            }
            control.setValidators([Validators.required]);
            control.updateValueAndValidity({ emitEvent: false });
            if (param.onChange) {
                param.destroy$ = new Subject<void>();

                control.valueChanges
                    .pipe(takeUntil(param.destroy$))
                    .subscribe((val) => {
                        param.onChange(formGroup, val);
                    });
            }
        });

        this.activeParams$.next(nextActive);
        this.lastActive = nextActive;
    }
}
